import React from "react";
import { Input } from "./input";
import { Button } from "./button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "./tooltip";
import { FileIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface FileUploadInputProps {
  accept?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  currentFileName?: string;
  onClearFile?: () => void;
  className?: string;
}

export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  accept,
  onChange,
  disabled = false,
  loading = false,
  currentFileName,
  onClearFile,
  className
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleClearFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClearFile?.();
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="relative flex-1">
        <Input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={onChange}
          disabled={disabled || loading}
          className={cn(
            "cursor-pointer",
            currentFileName &&
              "text-transparent [&::file-selector-button]:hidden"
          )}
          style={{
            backgroundImage: currentFileName
              ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z'/%3E%3Cpolyline points='14,2 14,8 20,8'/%3E%3C/svg%3E")`
              : "none",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "8px center",
            paddingLeft: currentFileName ? "32px" : "12px"
          }}
        />
        {currentFileName && (
          <div className="absolute inset-0 flex items-center pointer-events-none">
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 w-full">
              <FileIcon className="w-4 h-4 flex-shrink-0" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="truncate flex-1 min-w-0 cursor-help">
                      {currentFileName}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{currentFileName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
      </div>
      {currentFileName && onClearFile && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClearFile}
          disabled={disabled || loading}
          className="flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};
